import { useState, useEffect } from 'react';
import ReactApexChart from 'react-apexcharts';
import { TALENT, SELECT_STYLE, TALENT_ARR } from '../../data/define';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import ToolTip from '../common/ToolTip';
import { useTranslation } from '../../hook/useTranslation';

function ChangeFlowPreNextChart({
  changeToggle,
  compareResult = null,
  lastDate,
  currentDate,
  result = [],
}) {
  const { t } = useTranslation();
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [intelligence, setIntelligence] = useState(TALENT.RELATIONSHIP.id);
  const [editedResult, setEditedResult] = useState([]);
  const [preNextChartData, setPreNextChartData] = useState([
    {
      name: '',
      data: [null, 0, 0, null],
    },
  ]);

  useEffect(() => {
    if (result?.length > 0) {
      let totalResult: any = [];
      TALENT_ARR.forEach((talent) => {
        const existItem = result.find((item: any) => item.id === talent.id);
        if (existItem) {
          totalResult.push(existItem);
        } else {
          totalResult.push({
            id: talent.id,
            percentile: 0,
          });
        }
      });
      setEditedResult(totalResult);
    }
  }, [result]);

  // 지능순위 default 설정
  useEffect(() => {
    if (result && result?.length > 0) {
      const defaultResult = result.reduce((prev: any, current: any) => {
        return (prev.percentile || 0) > (current.percentile || 0) ? current : prev;
      });
      const defaultTalent = TALENT_ARR.find((item) => item.id === defaultResult.id);
      if (defaultTalent) {
        setIntelligence(defaultTalent.id);
      } else {
        setIntelligence(TALENT.RELATIONSHIP.id);
      }
    } else {
      setIntelligence(TALENT.RELATIONSHIP.id);
    }
  }, [result]);

  // 데이터 설정
  useEffect(() => {
    if (!compareResult) return;
    const baseTarget =
      compareResult.baseResult.find((i: any) => i.intelligence === intelligence)?.score ?? null;
    const currentTarget =
      compareResult.targetResult.find((i: any) => i.intelligence === intelligence)?.score ?? null;
    const chartResult = [null, baseTarget, currentTarget, null];
    setPreNextChartData([
      {
        name: '',
        data: chartResult,
      },
    ]);
  }, [changeToggle, compareResult, intelligence]);

  const preNextChartOptions = {
    chart: {
      type: 'line',
      toolbar: { show: false },
    },
    colors: ['#74DFE0'],
    grid: {
      show: true,
      padding: {
        left: 12,
        right: 12,
        top: 0,
        bottom: 0,
      },
      yaxis: {
        lines: {
          show: true,
        },
      },
      xaxis: {
        lines: {
          show: false,
        },
      },
    },
    stroke: {
      curve: 'straight',
      width: 3,
    },
    xaxis: {
      type: 'category',
      categories: ['', lastDate, currentDate, ''],
      position: 'bottom',
      labels: {
        formatter: (val: any) => (val === '' ? '' : val),
        offsetY: 0,
        style: {
          colors: ['transparent', '#969696', '#5EBEBB', 'transparent'],
          fontSize: '14px',
          fontWeight: '700',
          fontFamily: 'Pretendard, sans-serif',
        },
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      min: 0,
      max: 100,
      tickAmount: 5,
      labels: {
        show: false,
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    plotOptions: {
      line: {
        isSlopeChart: true,
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (val: any) => (val === null ? '' : `${val}`),
      textAnchor: 'start',
      offsetX: 0,
      offsetY: -10,
      style: {
        colors: ['#000'],
        fontSize: '16px',
        fontFamily: 'Pretendard, sans-serif',
      },
      background: {
        enabled: true,
        foreColor: '#fff',
        padding: 4,
        borderRadius: 4,
        borderWidth: 0,
        borderColor: '#74DFE0',
        opacity: 1,
      },
      dropShadow: {
        enabled: false,
      },
    },
    markers: {
      size: 7,
      colors: ['#74DFE0'],
    },
    legend: {
      show: false,
    },
    tooltip: {
      enabled: false,
    },
  };

  return (
    <div className="w-full relative pt-[20px] pb-[20px]">
      <div className="flex items-center justify-between gap-2 mb-[20px]">
        <div className="flex items-center gap-2">
          <div className="text-[#1C1C1C] font-[700] text-[20px] text-left]">{t('report.change_flow.title')}</div>
          <ToolTip
            open={tooltipOpen}
            setOpen={setTooltipOpen}
            text={t('report.change_flow.tooltip')}
          />
        </div>
        <Select
          variant="outlined"
          value={intelligence || ''}
          onChange={(e) => setIntelligence(e.target.value as any)}
          IconComponent={KeyboardArrowDownIcon}
          size="small"
          sx={{
            ...SELECT_STYLE,
            height: '38px',
            borderRadius: '5px',
            border: '1px solid #E0E0E0',
            minWidth: '110px',
            fontWeight: 400,
          }}
        >
          {editedResult?.length >= 1 &&
            editedResult?.map((intelli: any) => (
              <MenuItem
                key={intelli.id}
                value={intelli.id}
                sx={{ fontSize: '14px' }}
                disabled={intelli.percentile === 0}
              >
                <span className="text-[14px] text-[#363636]">
                  {t(`talent.${TALENT_ARR.find((item) => item.id === intelli.id)?.key}`)}
                </span>
              </MenuItem>
            ))}
        </Select>
      </div>
      <div className="w-full flex flex-col items-center justify-between">
        <div
          className="relative w-full"
          style={{ width: '100vw', marginLeft: '-10px', paddingRight: '20px' }}
        >
          <ReactApexChart
            options={preNextChartOptions as any}
            series={preNextChartData}
            type="line"
            height={300}
            width="100%"
          />
          <div className="flex flex-col absolute top-5 right-[20px] gap-[25px] text-[#C6C6C6] text-[14px] font-[460]">
            <span>100</span>
            <span>80</span>
            <span>60</span>
            <span>40</span>
            <span>20</span>
            <span>0</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ChangeFlowPreNextChart;
